import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DISCORD_URL } from 'src/apis/const';
import { Button, HeadDom } from 'src/components';
import { ButtonSize, ButtonStyle } from 'src/components/common/button';
import JoinOurCommunitySection, {
  JoinOurCommunitySectionSize,
} from 'src/components/common/join-our-community-section';
import { MODAL_TYPE } from 'src/components/common/modal/modal';
import useConnectWallet from 'src/hooks/use-connect-wallet';
import useModal from 'src/hooks/use-modal';
import useUser from 'src/hooks/use-user';
import { SWRConfig } from 'swr';
import { SWR } from '../../apis/swr';

interface HomeProps {
  fallback: Record<string, any>;
}
interface WaitlistErrorResponse {
  status_code: number;
  error_code: string;
  error_string: string;
  field_key: string;
  field_value?: string;
}
class WaitlistError extends Error {
  // name: string;
  // message: string;
  // stack?: string; - Error 인터페이스 프로퍼티들을 직접 쓰거나 아니면 상속해준다.
  response?: WaitlistErrorResponse;

  constructor(response: WaitlistErrorResponse) {
    super();
    this.response = response;
    this.message = response.error_string;
    if (response.field_key !== 'email') {
      this.message += '\n' + response.field_key;
    }
  }
}

const WAITLIST_ID = 9676;

const Broadcasters: React.FC = () => {
  const [renderConnectWalletModals, handleConnectWalletOpen] =
    useConnectWallet();
  const router = useRouter();
  const [user] = useUser();

  const [waitlistData, setWaitlistData] = useState<any>();

  useEffect(() => {
    (async () => {
      const data = await fetch(
        `https://api.getwaitlist.com/api/v1/waitlist?waitlist_id=${WAITLIST_ID}`,
      ).then((res) => res.json());
      console.log(data);
      setWaitlistData(data);
    })();
  }, []);

  const waiterCount = useMemo<number>(() => {
    return waitlistData ? waitlistData?.statistics?.total_signups : 0;
  }, [waitlistData]);

  const waitlistQuestions = useMemo<any>(() => {
    console.log(waitlistData?.questions);
    return waitlistData?.questions || [];
  }, [waitlistData]);

  const onSubmit = useCallback(async (email, answers) => {
    const data = await fetch('/waitlist/api/v1/signup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        waitlist_id: WAITLIST_ID,
        answers: answers,
      }),
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        console.log(data);
        if (data.status_code) {
          throw new WaitlistError(data);
        }
      })
      .catch((e) => {
        throw new Error(e);
      });
    // setWaitlistData(data);
    router.reload();
  }, []);

  const [renderJoinWaitlist, handleOpenJoinWaitlist] = useModal(
    MODAL_TYPE.JoinWaitlist,
    onSubmit,
  );

  return (
    <>
      <div>
        {renderConnectWalletModals()}
        <main>
          <section id="section1">
            <div className="bg">
              <div className="bg-wrap">
                <div className="container-1440">
                  <h2>
                    Transforming Your Network
                    <br />
                    into <span>Net Worth</span>
                  </h2>
                  <p className="main-text h7-regular">
                    Become a broadcaster and join the forefront of the new
                    marketing era
                  </p>
                  <div className="button-list">
                    <div>
                      <Button
                        label="Join Waitlist"
                        buttonStyle={ButtonStyle.GRADIENT}
                        buttonSize={ButtonSize.L}
                        isDisabled={waitlistQuestions.length == 0}
                        handleClick={() => {
                          handleOpenJoinWaitlist({ waitlistQuestions });
                        }}
                        style={{
                          padding: '16px 35px',
                          width: 500,
                        }}
                      />
                    </div>
                  </div>
                  {/* <p className="h7-regular italic">
                    <i>{waiterCount} users have already joined waitlist</i>
                  </p> */}
                </div>
              </div>
              <div className="bg-images">
                <div className="bg-shadow bg-top"></div>
                <div className="bg"></div>
                <div className="bg-shadow bg-bottom"></div>
              </div>
            </div>
          </section>
          <section id="section6">
            <div className="container-1440">
              <h3 className="h6-bold">Backed By</h3>
              <div className="bg"></div>
            </div>
          </section>
          <section id="section2">
            <div className="container-1440">
              <h3 className="h2-bold">Share-to-earn</h3>
              <p className="desc h6-regular">
                Introducing the “Broadcaster Program” - an innovative way to
                leverage your social influence to earn tokens.
              </p>
              <div className="detail-wrap">
                <div className="problem-wrap">
                  <div className="problem-card">
                    <img src="/image/broadcaster-problem-img-1.png"></img>
                    <p className="h7-regular">
                      Share your thoughts about the project on Social
                    </p>
                  </div>
                  <div className="problem-card">
                    <img src="/image/broadcaster-problem-img-2.png"></img>
                    <p className="h7-regular">
                      Monetize your impact and influence
                    </p>
                  </div>
                </div>
                <div className="arrow">
                  <img src="/image/broadcaster-problem-arrow.png"></img>
                </div>
                <div className="solution-wrap">
                  <p className="h4-bold">
                    Unlocking new opportunities by contributing to the{' '}
                    <span className="gradient">
                      healthier growth of communities
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section id="section3">
            <div className="container-1440">
              <h3 className="h2-bold">Your Journey as a Broadcaster</h3>
              <div className="step-wrap">
                <div className="step-card">
                  <img src="/image/ic_broadcaster_step1.svg"></img>
                  <h6 className="h6-bold step-title">
                    Step 1: Join and Select
                  </h6>
                  <p className="h7-regular step-text">
                    Begin your adventure as a broadcaster by joining us and
                    selecting a campaign that resonates with your interests.
                  </p>
                </div>
                <div className="step-card">
                  <img src="/image/ic_broadcaster_step2.svg"></img>
                  <h6 className="h6-bold step-title">
                    Step 2: Share and Inspire
                  </h6>
                  <p className="h7-regular step-text">
                    Amplify the reach of the campaign throughout your network
                    using your distinctive referral link.
                  </p>
                </div>
                <div className="step-card">
                  <img src="/image/ic_broadcaster_step3.svg"></img>
                  <h6 className="h6-bold step-title">Step 3: Earn Rewards</h6>
                  <p className="h7-regular step-text">
                    Earn rewards for every interaction and engagement that stems
                    from your dedicated efforts.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section id="section7">
            <div className="container-1440">
              <h3 className="h3-bold">Follow us for updates</h3>
              <div className="wrap">
                <div className="social-card">
                  <JoinOurCommunitySection
                    icon="/image/twitter-icon.png"
                    title="Twitter"
                    buttonLabel="Follow on Twitter"
                    joinOurCommunitySectionSize={JoinOurCommunitySectionSize.L}
                    handleOnClick={() => {
                      window
                        .open('https://twitter.com/noox_world', '_blank')
                        ?.focus();
                    }}
                  />
                </div>
                <div className="social-card">
                  <JoinOurCommunitySection
                    icon="/image/discord-icon-new.png"
                    title="Discord"
                    buttonLabel="Join our Discord"
                    joinOurCommunitySectionSize={JoinOurCommunitySectionSize.L}
                    handleOnClick={() => {
                      window.open(DISCORD_URL, '_blank')?.focus();
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        </main>
        {renderJoinWaitlist}
        <style global jsx>
          {`
            html,
            body {
              background: #0a0e2e !important;
              -webkit-font-smoothing: antialiased;
            }
            * {
              box-sizing: border-box;
            }
          `}
        </style>
        <style jsx>
          {`
            main {
              width: 100vw;
              overflow-x: hidden;
              margin: 0 auto;
              background-size: 1480px 932px;
              min-height: 100vh;
              position: relative;
              top: -40px;
              background-color: var(--G900);
            }
            .container-1440 {
              max-width: 1440px;
              margin: 0 auto;
              display: flex;
              flex-direction: column;
              align-items: center;
              padding: 100px 120px 60px;
              justify-content: center;
            }
            p {
              color: var(--G200);
            }
            h3 {
              margin: 0 auto 10px;

              text-shadow: 2px 2px rgba(0, 0, 0, 0.3);
              text-align: center;
            }
            .bg {
              width: 100%;
            }
            .italic {
              font-style: italic;
            }
            .desc {
              text-align: center;
            }
            #section1 {
              background-color: var(--B900);
            }

            #section1 .bg {
              position: relative;
              min-height: 981px;
            }
            #section1 .container-1440 {
              align-items: center;
              display: flex;
              flex-direction: column;
              gap: 25px;
              justify-content: flex-start;
              padding-bottom: 0;
            }
            #section1 .container-1440 h2 {
              font-size: 7.2rem;
              line-height: 10.8rem;
              text-align: center;
              font-weight: 700;
              width: 1200px;
            }
            #section1 .container-1440 h2 span {
              background: var(--GR-NEW-PP);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }

            #section1 .bg-images {
              width: 100%;
              background-size: 1920px 544px;
              min-height: 544px;
              position: absolute;
              bottom: 0;
            }

            #section1 .bg-images .bg {
              position: absolute;
              background: url(/image/landing_5_img.png) top center no-repeat;
              background-size: 1920px 544px;
              min-height: 544px;
              z-index: 1;
            }
            #section1 .bg-images .bg-shadow {
              position: absolute;
              background: url(/image/landing_5_img_bg.png) top center no-repeat;
              background-size: 1920px 544px;
              min-height: 544px;
              width: 100%;
            }
            #section1 .bg-images .bg-top {
              z-index: 2;
              mix-blend-mode: overlay;
            }
            #section1 .bg-images .bg-bottom {
              z-index: 0;
              mix-blend-mode: overlay;
            }
            #section2 {
              background: radial-gradient(
                  71.05% 50% at 53.54% 50.04%,
                  rgba(40, 179, 162, 0.2) 0%,
                  rgba(23, 19, 44, 0.2) 100%
                ),
                #17132c;
            }
            #section2 .container-1440 {
              gap: 32px;
            }
            #section2 .detail-wrap {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 20px;
              width: 100%;
            }
            #section2 .problem-wrap {
              display: flex;
              gap: 12px;
              align-items: center;
              height: 150px;
              justify-content: center;
            }
            #section2 .problem-card {
              padding-left: 32px;
              display: flex;
              width: 350px;
              gap: 8px;
              align-items: center;
            }
            #section2 .problem-card p {
              color: var(--G25);
            }
            #section2 .problem-card img {
              width: 60px;
              height: 60px;
            }
            #section2 .arrow {
              width: 196px;
              height: 57px;
            }
            #section2 .solution-wrap {
              color: var(--G25);
              padding: 60px 40px;
              background-image: linear-gradient(var(--G900), var(--G900)),
                linear-gradient(270deg, #eeadff 0%, #bea8ff 100%);
              border: 4px solid transparent;
              background-origin: border-box;
              background-clip: padding-box, border-box;
              border-radius: 40px;
              width: 100%;
            }
            #section2 .solution-wrap .solution {
            }
            #section2 .desc {
              max-width: 900px;
            }
            #section2 .solution-wrap p {
              color: var(--G25);
              text-align: center;
              margin: 0 auto;
              max-width: 900px;
              text-shadow: 0px 4px 12px rgba(227, 188, 255, 0.5);
            }
            #section2 .solution-wrap p > span {
              background: var(--GR-NEW-PP);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
            #section3 {
              background: radial-gradient(
                71.05% 50% at 53.54% 50.04%,
                rgba(120, 84, 247, 0.2) 0%,
                rgba(23, 19, 44, 0.2) 100%
              );
            }
            #section3 .container-1440 {
              gap: 32px;
            }
            #section3 .step-wrap {
              display: flex;
              gap: 20px;
            }
            #section3 .step-wrap .step-card {
              flex: 1;
              gap: 20px;
              display: flex;
              flex-direction: column;
              padding: 24px 28px;
              border-radius: 40px;
              background-image: linear-gradient(var(--G900), var(--G900)),
                var(--GR-PG);
              border: 2px solid transparent;
              background-origin: border-box;
              background-clip: padding-box, border-box;
            }
            #section3 .step-wrap .step-card img {
              width: 60px;
              height: 60px;
            }
            #section3 .step-card .step-title {
              color: var(--LG);
            }
            #section3 .step-card .step-text {
              color: var(--G25);
            }
            #section4 {
              width: 100%;
              min-height: 820px;
            }
            #section4 .container-1440 .bg {
              background: url(/image/landing_4_img.png) top center no-repeat;
              background-size: 1440px 643px;
              min-height: 643px;
            }

            #section6 {
              width: 100%;
            }
            #section6 .container-1440 {
              gap: 10px;
            }
            #section6 .container-1440 .bg {
              background: url(/image/logo-bg.png) center center no-repeat;
              background-size: 1100px 62.33px;
              width: 1100px;
              height: 62.33px;
              margin: 0 auto;
            }
            #section7 {
              width: 100%;
              background: radial-gradient(
                71.05% 50% at 53.54% 50.04%,
                rgba(120, 84, 247, 0.2) 0%,
                rgba(23, 19, 44, 0.2) 100%
              );
              margin-bottom: 100px;
            }
            #section7 .container-1440 {
              flex-direction: column;
              display: flex;
              gap: 32px;
            }
            #section7 .wrap {
              display: flex;
              width: 100%;
              margin: 0 auto;
              flex-wrap: wrap;
              gap: 16px;
              justify-content: center;
            }
            #section7 .social-card {
              flex: 1;
              background-color: var(--G900);
              border-radius: 28px;

              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
            }

            @media (max-width: 1440px) {
              main {
                min-width: 0px;
                width: 100%;
              }
              .container-1440 {
                padding-left: 60px;
                padding-right: 60px;
              }
            }

            @media (min-width: 1440px) {
              main {
              }

              #section2 .container-1440:first-of-type .bg {
                background: url(/image/landing_2_img_1_2x.png) top center
                  no-repeat;
              }
              #section2 .container-1440:first-of-type .bg-shadow {
                background: url(/image/landing_2_img_1_bg_2x.png) top center
                  no-repeat;
              }
              #section2 .container-1440:first-of-type .bg-wrap,
              #section2 .container-1440:first-of-type .bg,
              #section2 .container-1440:first-of-type .bg-shadow {
                background-size: 100% auto;
                height: calc(100vw / 1440 * 753);
              }
              #section2 .container-1440:last-of-type .bg {
                background: url(/image/landing_2_img_2_2x.png) top center
                  no-repeat;
                background-size: 100% auto;
                height: calc(100vw / 1440 * 531);
              }

              #section4 .container-1440 {
                width: 100%;
                height: calc(100vw / 1440 * 820);
              }
              #section4 .container-1440 .bg {
                background: url(/image/landing_4_img_2x.png) top center
                  no-repeat;
                background-size: 100% auto;
                height: calc(100vw / 1440 * 643);
              }
              #section5 .container-1440 {
                width: 100%;
                height: calc(100vw / 1440 * 654);
              }
              #section5 .container-1440 .bg {
                background: url(/image/landing_5_img_2x.png) top center
                  no-repeat;
                background-size: 100% auto;
                height: calc(100vw / 1440 * 409);
              }
              #section6 {
              }
            }
          `}
        </style>
      </div>
    </>
  );
};

const BroadcastersPage: NextPage<HomeProps> = () => {
  let url;
  if (process.env.NODE_ENV === 'production') {
    url = 'https://noox.world';
  } else {
    url = 'https://dev-test.noox.world';
  }
  return (
    <SWRConfig value={{ ...(SWR.GLOBAL_SETTING as any) }}>
      <HeadDom
        title="Join the Broadcaster Early Access Waitlist"
        description="Become a broadcaster and join the forefront of the new marketing era. Join our early access waitlist."
        url={`${url}/broadcasters`}
        ogImage={`${url}/opengraph_Nooxbroadcaster.png`}
      ></HeadDom>
      <Broadcasters />
    </SWRConfig>
  );
};

export default BroadcastersPage;
